// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// "manager" sees and edits everything an admin does, except the users table.
type Role = "admin" | "manager" | "operator";

type JwtPayload = {
  sub?: string;
  role?: Role;
  roles?: string[]; // tolerate array-style roles
  scope?: string; // tolerate space-delimited scopes
  exp?: number; // seconds since epoch
  [k: string]: unknown;
} | null;

interface AuthContextType {
  // raw
  token: string | null;

  // derived
  isAuthenticated: boolean;
  role: Role | null;
  userEmail: string | null;
  isAdmin: boolean;
  isManager: boolean;
  isOperator: boolean;
  /** Admin-level access: every page and every edit except user management. */
  hasAdminAccess: boolean;
  /** Strictly admins — managers are locked out of the users table. */
  canManageUsers: boolean;

  // actions
  login: (token: string) => void;
  logout: () => void;
}

// ---- helpers
function base64UrlDecode(input: string): string {
  const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function decodeJwt(token: string): JwtPayload {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payloadJson = base64UrlDecode(parts[1]);
    return JSON.parse(payloadJson);
  } catch {
    return null;
  }
}

// allow a little clock skew (default 60s)
const isExpired = (exp?: number, skewSeconds = 60) =>
  exp ? Date.now() >= (exp - skewSeconds) * 1000 : false;

// normalize role from multiple possible places
function extractRole(decoded: JwtPayload): Role | null {
  if (!decoded) return null;

  // Most privileged first, so a token carrying several roles resolves upward.
  const KNOWN: Role[] = ["admin", "manager", "operator"];

  // direct
  if (KNOWN.includes(decoded.role as Role)) return decoded.role as Role;

  // array
  if (Array.isArray(decoded.roles)) {
    const match = KNOWN.find((r) => decoded.roles!.includes(r));
    if (match) return match;
  }

  // scope string like "read write operator"
  if (typeof decoded.scope === "string") {
    const scopes = decoded.scope.split(/\s+/);
    const match = KNOWN.find((r) => scopes.includes(r));
    if (match) return match;
  }

  return null;
}

// ---- context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem("token")
  );

  // keep localStorage in sync
  useEffect(() => {
    if (token) localStorage.setItem("token", token);
    else localStorage.removeItem("token");
  }, [token]);

  const decoded = useMemo(() => (token ? decodeJwt(token) : null), [token]);
  const role = useMemo(() => extractRole(decoded), [decoded]);
  const userEmail = decoded?.sub ?? null;

  const logout = () => setToken(null);
  const login = (t: string) => setToken(t);

  // auto-logout only for truly invalid or expired tokens
  useEffect(() => {
    if (!token) return;

    // malformed payload -> logout
    if (!decoded) {
      logout();
      return;
    }

    // expired -> logout
    if (isExpired(decoded.exp)) {
      logout();
    }
  }, [token, decoded]);

  const isAuthenticated = Boolean(token && decoded && !isExpired(decoded?.exp));
  const isAdmin = role === "admin";
  const isManager = role === "manager";
  const hasAdminAccess = isAdmin || isManager;
  const canManageUsers = isAdmin;
  const isOperator = role === "operator" || hasAdminAccess;

  const value = useMemo<AuthContextType>(
    () => ({
      token,
      isAuthenticated,
      role,
      userEmail,
      isAdmin,
      isManager,
      isOperator,
      hasAdminAccess,
      canManageUsers,
      login,
      logout,
    }),
    [
      token,
      isAuthenticated,
      role,
      userEmail,
      isAdmin,
      isManager,
      isOperator,
      hasAdminAccess,
      canManageUsers,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
